import React from 'react';
import { BarChart2, PieChart } from 'lucide-react';

// Mini Sparkline
const SparkLine = ({ data = [], color = '#38bdf8', height = 40, width = 180 }) => {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 6) - 3;
    return `${x},${y}`;
  });
  const id = `sg${color.replace(/[^a-z0-9]/gi, '')}`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-hidden">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${height} ${pts.join(' ')} ${width},${height}`} fill={`url(#${id})`} />
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
};

// Graphique d'occupation (barres)
const OccupancyChart = ({ data = [] }) => {
  if (!data.length) return (
    <div className="h-48 flex items-center justify-center text-gray-600 text-sm">No data</div>
  );

  const maxVal = Math.max(...data.flatMap(d => [d.occupied, d.available]), 1);
  const W = 560, H = 140, pad = 32, barW = Math.max(10, (W - pad * 2) / data.length - 10);

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${W} ${H + 30}`}
      className="overflow-hidden"   // ✅ était overflow-visible
      preserveAspectRatio="xMidYMid meet"
    >
      {[0, 0.25, 0.5, 0.75, 1].map(t => (
        <g key={t}>
          <line x1={pad} y1={H * (1 - t)} x2={W - pad} y2={H * (1 - t)}
            stroke="#1e293b" strokeWidth="1" />
          <text x={pad - 4} y={H * (1 - t) + 4} textAnchor="end" fill="#475569" fontSize="8">
            {Math.round(maxVal * t)}
          </text>
        </g>
      ))}
      {data.map((d, i) => {
        const slotW = (W - pad * 2) / data.length;
        const x = pad + i * slotW + (slotW - barW * 2 - 2) / 2;
        const hOcc   = (d.occupied  / maxVal) * H;
        const hAvail = (d.available / maxVal) * H;
        return (
          <g key={i}>
            <rect x={x}           y={H - hOcc}   width={barW} height={hOcc}   fill="#10b981" rx="2" opacity="0.85" />
            <rect x={x + barW + 2} y={H - hAvail} width={barW} height={hAvail} fill="#f59e0b" rx="2" opacity="0.75" />
            <text x={x + barW} y={H + 14} textAnchor="middle" fill="#475569" fontSize="7">
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// Donut chart
const LeaseTypeDonut = ({ data = [], size = 150 }) => {
  const total = data.reduce((s, d) => s + d.count, 0) || 1;
  const r = 52, circ = 2 * Math.PI * r;
  let offset = circ * 0.25;

  return (
    <svg width={size} height={size} viewBox="0 0 120 120">
      <circle cx="60" cy="60" r={r} fill="none" stroke="#1e293b" strokeWidth="16" />
      {data.map((d, i) => {
        const arc = (d.count / total) * circ;
        const el = (
          <circle key={i} cx="60" cy="60" r={r} fill="none"
            stroke={d.color} strokeWidth="16"
            strokeDasharray={`${arc} ${circ}`}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 1s ease' }} />
        );
        offset -= arc;
        return el;
      })}
      <text x="60" y="56" textAnchor="middle" fill="white" fontSize="11" fontWeight="bold">
        {total}
      </text>
      <text x="60" y="68" textAnchor="middle" fill="#64748b" fontSize="7">TOTAL LEASES</text>
    </svg>
  );
};

const HangarLeaseCharts = ({ occupancyHistory, leaseTypeBreakdown, monthlyRevenueHistory }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

      {/* ── Graphique occupation ── */}
      <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4 overflow-hidden min-w-0">
        {/* ✅ overflow-hidden + min-w-0 empêchent le débordement dans la grille */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <BarChart2 size={15} className="text-emerald-400" />
            Hangar Occupancy Trends
          </h3>
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <span className="w-3 h-2 rounded-sm bg-emerald-400 inline-block opacity-85" />Occupied
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-2 rounded-sm bg-amber-400 inline-block opacity-75" />Available
            </span>
          </div>
        </div>
        <div className="w-full overflow-hidden"> {/* ✅ wrapper supplémentaire */}
          <OccupancyChart data={occupancyHistory} />
        </div>
      </div>

      {/* ── Répartition types de baux ── */}
      <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4 overflow-hidden min-w-0">
        <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
          <PieChart size={15} className="text-purple-400" />
          Lease Type Distribution
        </h3>
        <div className="flex items-center justify-center">
          <LeaseTypeDonut data={leaseTypeBreakdown} size={150} />
        </div>
        <div className="mt-3 space-y-2">
          {leaseTypeBreakdown.map(type => (
            <div key={type.type} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: type.color }} />
                <span className="text-gray-300 capitalize">{type.type}</span>
              </div>
              <span className="text-gray-400">{type.count} leases</span>
              <span className="font-semibold" style={{ color: type.color }}>{type.pct}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Revenus mensuels ── */}
      {monthlyRevenueHistory && monthlyRevenueHistory.length > 0 && (
        <div className="lg:col-span-2 bg-gray-800/60 border border-gray-700/60 rounded-xl p-4 overflow-hidden min-w-0">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-white font-semibold text-sm flex items-center gap-2">
              <BarChart2 size={15} className="text-blue-400" />
              Monthly Rental Revenue Trend
            </h3>
          </div>
          <div className="flex justify-center w-full overflow-hidden">
            <SparkLine data={monthlyRevenueHistory} color="#38bdf8" height={60} width={500} />
          </div>
          <div className="flex justify-between text-gray-500 text-xs mt-2 px-4">
            {monthlyRevenueHistory.map((_, i) => (
              <span key={i}>M-{monthlyRevenueHistory.length - i}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HangarLeaseCharts;